import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { _T } from 'appI18n';
import { useHotelRecommendations, useTourDescription, useTourVariant } from '../../../TourSearch/store/TourDescriptionStore';
import DescriptionMenu from './DescriptionMenu';
import ExcursionDescription from './ExcursionDescription';
import ExtraServiceDescription from './OtherServices/ExtraServiceDescription';
import { HotelDescription } from './HotelDescription';
import { HotelRecommendationsContainer } from './HotelRecommendations';
import { HotelRoomVariantsContainer } from './HotelRoomVariants';
import { LightboxImageGrid } from './LightboxImageGrid';
import Section from './Section';
import TourProgram from './TourProgram';
import TourSidebar from './TourSidebar';
import TransferDescription from './OtherServices/TransferDescription';
import { TourGeneralInfo } from './TourGeneralInfo';
import { useRoomStyles } from './HotelRoomVariants/HotelRoomVariants';
import moment from 'moment';
import { Wrapper } from '@googlemaps/react-wrapper';
import { FlightVariants } from './FlightVariants/FlightVariants';
import { ExtrasContainer } from './Extras/ExtrasContainer';
import { _ParentFrame } from '../../../Frame/types/Frame';
const useStyles = makeStyles(theme => ({
    sidebar: {
        position: 'sticky',
        top: 70,
    },
    mainGrid: {
        [theme.breakpoints.down("sm")]: {
            flexDirection: 'column-reverse'
        }
    },
    mainGridColumns: {
        [theme.breakpoints.down("sm")]: {
            maxWidth: '100%'
        }
    },
}));
export default function TourDescription({ isFrame, bookCallback, style }) {
    var _a;
    const [state] = useTourDescription(null, isFrame);
    const classes = useStyles();
    const roomClasses = useRoomStyles();
    const { tourDescription, tourEvents, excursionServices, transferServices, flightServices, extraServices, hotelServices, hotelDescriptions, hash, price } = state;
    const [{ tourVariant }] = useTourVariant(isFrame);
    const [recommendations] = useHotelRecommendations(isFrame);
    const [hotelRefs, setHotelRefs] = useState([]);
    useEffect(() => {
        setHotelRefs(hotelDescriptions.map(() => createRef()));
    }, hotelDescriptions);
    const descriptionRef = useRef(null);
    //const hotelRef = useRef(null);
    const itineraryRef = useRef(null);
    const flightsRef = useRef(null);
    const excursionsRef = useRef(null);
    const transfersRef = useRef(null);
    const extrasRef = useRef(null);
    const tabs = useMemo(() => [
        { d: { label: _T.COMMON_DESCRIPTION, ref: descriptionRef }, visible: true },
        ...(hotelDescriptions.length > 1 ? hotelDescriptions.map((hd, i) => ({ d: { label: `${_T.COMMON_HOTEL}: ${hd.hotelDescription.hotelName}`, ref: hotelRefs[i] }, visible: true })) : [{ d: { label: _T.COMMON_HOTEL, ref: hotelRefs[0] }, visible: !!hotelDescriptions.length }]),
        { d: { label: _T.COMMON_ITINERARY, ref: itineraryRef }, visible: !!tourEvents.length },
        { d: { label: _T.COMMON_FLIGHTS, ref: flightsRef }, visible: !!flightServices.length },
        { d: { label: _T.COMMON_EXCURSIONS, ref: excursionsRef }, visible: !!excursionServices.length },
        { d: { label: _T.TOUR_OTHER_SVCS_SHORT, ref: transfersRef }, visible: !!transferServices.length },
        { d: { label: _T.COMMON_EXTRA_SERVICES, ref: extrasRef }, visible: !!(price === null || price === void 0 ? void 0 : price.extras.length) },
    ].filter(({ visible }) => visible).map(({ d }) => d), [
        tourEvents.length,
        excursionServices.length,
        descriptionRef,
        hotelRefs,
        itineraryRef,
        flightsRef,
        excursionsRef,
        transfersRef,
        flightServices,
        extrasRef,
    ]);
    return (React.createElement(Wrapper, { apiKey: window._GMAPS_API_KEY },
        React.createElement("div", { style: style },
            React.createElement(DescriptionMenu, { items: tabs, isFrame: isFrame }),
            React.createElement(Box, { mt: 3, mb: 3 },
                React.createElement(Grid, { container: true, justify: "space-between" },
                    React.createElement(Grid, { item: true, xs: "auto" },
                        React.createElement(Typography, { variant: "h3", component: "h1" }, tourDescription.name)))),
            React.createElement(Grid, { container: true, spacing: 3, className: classes.mainGrid },
                React.createElement(Grid, { item: true, sm: 12, md: 8, className: classes.mainGridColumns },
                    ((_a = tourDescription.images) === null || _a === void 0 ? void 0 : _a.length) ?
                        React.createElement(Box, { mb: 3 },
                            React.createElement(LightboxImageGrid, { images: tourDescription.images, imageGridCellHeight: 140, onImageClick: isFrame ? (index) => {
                                    const parentFrame = _ParentFrame();
                                    parentFrame.sendMessage({ message: 'open-gallery', params: { initialIndex: index } });
                                } : undefined })) : null,
                    React.createElement(Section, { ref: descriptionRef, id: "tour-description-section", variant: "sub" },
                        React.createElement(TourGeneralInfo, { description: tourDescription })),
                    flightServices.length &&
                        React.createElement(Section, { title: _T.COMMON_FLIGHTS, ref: flightsRef, id: "tour-flights-section" },
                            React.createElement(FlightVariants, { flights: flightServices, isFrame: isFrame })) || null,
                    tourVariant && hotelDescriptions.length && (!recommendations.initialized || (recommendations.recommendations && recommendations.recommendations.length > 1)) &&
                        React.createElement(Section, { title: _T.TOUR_HOTEL_VARIANTS, variant: "sub" },
                            React.createElement(HotelRecommendationsContainer, { isFrame: isFrame, hash: hash })) || null,
                    hotelDescriptions.map(({ hotelDescription, externalHotelPrice }, i) => (React.createElement(Section, { title: `${_T.COMMON_HOTEL} ${hotelDescription.hotelName} ${moment(hotelServices[i].beginDate).format("L")} (${moment(hotelServices[i].beginDate).format("dd")})`, ref: hotelRefs[i], id: `tour-hotel-description-${i}`, key: i },
                        React.createElement(Box, { mb: 3 },
                            React.createElement(HotelDescription, { description: hotelDescription, externalHotelPrice: externalHotelPrice, isFrame: !!isFrame })),
                        tourVariant &&
                            React.createElement(Section, { title: _T.TOUR_HOTEL_ROOM_VARIANTS, variant: "sub" },
                                React.createElement(HotelRoomVariantsContainer, { isFrame: isFrame, hash: hash, hotelIndex: i })) || null))),
                    tourEvents.length &&
                        React.createElement(Section, { title: _T.COMMON_ITINERARY, ref: itineraryRef, id: "tour-program-section" },
                            React.createElement(TourProgram, { events: tourEvents })) || null,
                    excursionServices.length &&
                        React.createElement(Section, { title: _T.COMMON_EXCURSIONS, ref: excursionsRef, id: "tour-excursions-section" }, excursionServices.map(x => React.createElement(Box, { mb: 1, key: x.id },
                            React.createElement(ExcursionDescription, { service: x, isFrame: isFrame, hash: hash })))) || null,
                    React.createElement(Section, { title: _T.TOUR_OTHER_SVCS, ref: transfersRef, id: "tour-other-services-section" },
                        transferServices.length && React.createElement(TransferDescription, { services: transferServices }) || null,
                        extraServices.length && React.createElement(ExtraServiceDescription, { services: extraServices }) || null),
                    (price === null || price === void 0 ? void 0 : price.extras.length) &&
                        React.createElement(Section, { title: _T.COMMON_EXTRA_SERVICES, ref: extrasRef, id: "tour-extras-section" },
                            React.createElement(ExtrasContainer, { isFrame: isFrame, hash: price === null || price === void 0 ? void 0 : price.servicesHash })) || null),
                React.createElement(Grid, { item: true, sm: 12, md: 4, className: classes.mainGridColumns },
                    React.createElement(TourSidebar, { classes: { root: classes.sidebar }, isFrame: isFrame, bookCallback: bookCallback, hash: hash }))))));
}
